import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LoadingButton } from '@mui/lab';
import { Button, CssBaseline, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import loader from 'src/components/LoaderImg/loader.gif';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import { Seo } from 'src/components/seo';
import {
  copyCodeDescriptionTreatment,
  dsnDeletePermissionTreatment,
  multiPayerFlag,
} from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import useFeatureToggle, { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import EmptyState from 'src/pages/details-page/unlockTherapist/Placeholder.svg';
import {
  deleteDailySessionNoteAction,
  getNoteTemplateAction,
  resetDailySessionSoapNotes,
} from 'src/redux/actions/dailySessionNotes';
import {
  pullUuidAction,
  resetForLoaderDocumentation,
  resetUuidAction,
} from 'src/redux/actions/documentationAction';
import CopyTextBank from '../copy-text-bank';
import { DrawerMain } from '../copy-text-bank/drawer-configuration';
import ReForm from '../reform';
import SucessModal from '../sucess-modal';
import BackModalConfirmation from './back-modal-confirmation';
import DeleteNoteConfirmation from './delete-note-confirmation';
import { isDeletableDsnStatus } from './dsn-delete-eligibility';
import { getDSNNoteSummary, resetDsnSummary } from 'src/redux/actions/therapist-dashboard-action';
import DSNSummaryModal from './DSN summary/DSNSummaryModal';
import moment from 'moment';
// THIN-7916: facility-local today for the future-date guard on Start Note.
import { getFacilityTodayYmd } from 'src/utils/convertDateToSelectedTimeZone';
import { paths } from 'src/paths';
import { TrashIcon } from '../therapyPlanning_2.0/commonComponent/therapy-planning-icons';
import { dsnPageTestId } from './test-ids';

const loaderCSS: CSSProperties = {
  position: 'fixed',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  WebkitUserSelect: 'none',
  KhtmlUserSelect: 'none',
  MozUserSelect: 'none',
  userSelect: 'none',
  zIndex: 9999,
};

type Props = {
  dailySessionNotesInfo: any;
  selectedPatient: any;
  setOpen: any;
  dailySessionNotesInfoResponse: any;
  setOpenEditNotes: any;
  createSoapNotes: any;
  selectedDate: any;
  isInClosedPeriod?: boolean;
  onClosedPeriodAction?: () => void;
};

function FinalizeNotes({
  dailySessionNotesInfo,
  selectedPatient,
  setOpen,
  dailySessionNotesInfoResponse,
  setOpenEditNotes,
  createSoapNotes,
  selectedDate,
  isInClosedPeriod = false,
  onClosedPeriodAction,
}: Props) {
  const dispatch = useDispatch();
  const getNoteTemplate = useSelector((state: any) => state.getNoteTemplateReducer);
  const pullUuidResponse = useSelector((state: any) => state.pullUuidRducder);
  const isDocLoader = useSelector((state: any) => state.loaderDocumenattionReducer);
  const soapNotesResponse = useSelector((state: any) => state?.updateSoapNoteReducer);
  const selectedPatientName = `${selectedPatient?.lastName} ${selectedPatient?.firstName}`;
  const lockOtherPatient = useSelector((state: any) => state.getLockDailySessionNote);
  const getNoteResponse = useSelector((state: any) => state?.getDailySessionNotes);
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  const previousUuidRef = useRef<string | undefined>();
  const [payerPresent, setPayerPresent] = useState(false);
  const [isMultiPayer] = useFeatureToggleWhenReady(multiPayerFlag, 'FACILITY');
  //  THIN-7799: the delete affordance needs the treatment ON.
  //  Fails closed — the hook returns null until the Split SDK is ready.
  const [isDsnDeleteEnabled] = useFeatureToggleWhenReady(
    dsnDeletePermissionTreatment,
    'ENTERPRISE'
  );
  //  THIN-7806: a note dated inside a CLOSED month-end period is not deletable — the only
  //  eligibility blocker per the THIN-7634 grooming decision. The EoC list already hides
  //  Delete for these via the BE `deletable` flag (THIN-7789/THIN-7636); this page never
  //  checked, so it offered a closed-period bypass the BE then rejected. Folded into
  //  canDelete rather than the JSX so every delete affordance on this page inherits it.
  //  BOPS-446: no permission check — the button also requires the note's BE `deletable` flag,
  //  which is the server's answer for this user (Any key, or Own key on a note they wrote), so
  //  an own-only user sees Delete on their own notes and not on anyone else's.
  const canDelete = Boolean(isDsnDeleteEnabled) && !isInClosedPeriod;
  // Facility name for the delete-confirmation context line: match the active
  // facilityId against the assigned-facilities dropdown (there's no facility name
  // on the profile).
  const getAssignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);
  const facilityName =
    getAssignedFacilities?.response?.data?.data?.find(
      (fac: any) => fac?.uuid === localStorage.getItem('facilityId')
    )?.name ?? '';

  const handleCloseModal = useCallback(() => {
    dispatch(resetDailySessionSoapNotes());
  }, []);

  const handleTakeNoteAnyway = () => {
    setPayerPresent(true);
  };

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const deleteDsnState = useSelector((state: any) => state?.deleteDailySessionNoteReducer);
  // DSN uuid = dailySessionNoteUuid on the document (not the form-submission uuid).
  const dsnUuid = dailySessionNotesInfo?.dailySessionNote?.uuid;

  const handleDeleteNote = () => {
    //  THIN-7802: a closed month is the only eligibility blocker (THIN-7634 grooming), and
    //  the BE rejects the delete outright. Route to the same explanation dialog the submit
    //  path uses rather than silently doing nothing.
    if (isInClosedPeriod) {
      onClosedPeriodAction?.();
      return;
    }
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteNote = (reason: string) => {
    // reason is required + non-blank (BE 400s otherwise); the modal already enforces this.
    dispatch(deleteDailySessionNoteAction({ dsnUuid, reason }));
  };

  useEffect(() => {
    if (deleteDsnState?.isSuccess) {
      // Close + exit the editor. The patient-list refetch, toast and the reducer
      // reset are owned by PatientListing (which holds the list-fetch payloads).
      setOpenDeleteModal(false);
      setOpenEditNotes(false);
    }
  }, [deleteDsnState?.isSuccess]);

  useEffect(() => {
    const currentUuid = selectedPatient?.uuid;

    if (previousUuidRef.current !== currentUuid) {
      setPayerPresent(false);
      previousUuidRef.current = currentUuid;
    }
  }, [selectedPatient]);

  const [isCopyCodeDescriptionEnabled] = useFeatureToggle(
    copyCodeDescriptionTreatment,
    'ENTERPRISE'
  );

  const [discipline, firstName, lastName] = useCurrentUser(['discipline', 'firstName', 'lastName']);

  const getCleanUuid = () => {
    return pullUuidResponse?.response?.trim() || '';
  };

  useEffect(() => {
    if (!dailySessionNotesInfo?.dailySessionNote?.uuid) return;
    dispatch(getNoteTemplateAction(dailySessionNotesInfo?.dailySessionNote?.uuid));
  }, [dispatch, dailySessionNotesInfo, selectedPatient]);

  useEffect(() => {
    if (!dailySessionNotesInfo?.prefillKey?.split('/')[3]) return;
    if (dailySessionNotesInfo?.prefillKey?.split('/')[3]) {
      dispatch(
        pullUuidAction(
          {
            notesId: dailySessionNotesInfo?.dailySessionNote?.uuid,
            formId: dailySessionNotesInfo?.prefillKey?.split('/')[3],
            entityType: 'SOAP',
          },
          {
            forEdit: dailySessionNotesInfo?.dailySessionNote?.status === 'FINALIZED' ? true : false,
            discipline,
          }
        )
      );
      return;
    }
  }, [dailySessionNotesInfo, discipline, dispatch]);

  useEffect(() => {
    // BRK-1922: notes no longer get a Jotform prefill key, so most notes now mint their uuid here
    // rather than in the prefill-key effect above. Send the same forEdit/discipline so reopening a
    // finalized note still re-syncs its CPT codes with the plan of care (postUniqueId).
    const editParams = {
      forEdit: dailySessionNotesInfo?.dailySessionNote?.status === 'FINALIZED' ? true : false,
      discipline,
    };

    if (
      getNoteTemplate?.response?.data?.formId &&
      !dailySessionNotesInfo?.prefillKey?.split('/')[3]
    ) {
      dispatch(
        pullUuidAction(
          {
            notesId: dailySessionNotesInfo?.dailySessionNote?.uuid,
            formId: getNoteTemplate?.response?.data?.formId,
            entityType: 'SOAP',
          },
          editParams
        )
      );
    }

    if (
      getNoteTemplate?.response?.data?.reformFormId &&
      !dailySessionNotesInfo?.prefillKey?.split('/')[3]
    ) {
      const formId: string = getNoteTemplate?.response?.data?.link?.split('/')[3] || '';
      dispatch(
        pullUuidAction(
          {
            notesId: dailySessionNotesInfo?.dailySessionNote?.uuid,
            formId,
            entityType: 'SOAP',
          },
          editParams
        )
      );
    }
  }, [getNoteTemplate?.response?.data]);

  useEffect(() => {
    if (!isDocLoader.loader && isDocLoader.response === null) return;
    if (!isDocLoader.loader) {
      dispatch(resetUuidAction());
      dispatch(resetForLoaderDocumentation());
      setOpenEditNotes(false);
    }
  }, [isDocLoader]);

  const [openBackModal, setBackModal] = useState(false);
  const handleBack = () => {
    setBackModal(true);
  };

  const handleCloseBack = () => {
    setBackModal(false);
  };

  const [openTextBank, setOpenTextBank] = React.useState(false);

  const handleTextBankOpen = () => {
    setOpenTextBank(true);
  };

  const handleTextBankClose = () => {
    setOpenTextBank(false);
  };

  const dsnSummary = useSelector((state: any) => state.getDsnSummaryReducer);
  const [openSummary, setOpenSummary] = useState(false);

  const handleOpenDsnSummary = (patient: any) => {
    dispatch(resetDsnSummary());
    dispatch(
      getDSNNoteSummary({
        patientId: patient?.uuid,
        type: 'dashboard',
        params: {
          discipline: discipline,
          localDate: moment(selectedDate).format('YYYY-MM-DD'),
        },
      })
    );
    setOpenSummary(true);
  };

  const formElement = () => {
    if (getNoteTemplate?.loading) {
      return (
        <img
          src={loader}
          alt="Loading..."
        />
      );
    }

    return (
      <Box
        style={{
          width: openTextBank ? '125%' : '100%',
        }}
        ref={formContainerRef}
      >
        <ReForm
          uuid={getCleanUuid()}
          formId={
            getNoteTemplate?.response?.data?.reformFormId || getNoteTemplate?.response?.data?.formId
          }
          disablePadding={true}
          selectedPatient={selectedPatient}
          onSubmitBlocked={isInClosedPeriod ? onClosedPeriodAction : undefined}
        />
      </Box>
    );
  };

  return dailySessionNotesInfoResponse?.loading ? (
    <img
      src={loader}
      alt="Loading..."
      style={{ margin: 'auto', display: 'block', marginTop: '10%' }}
    />
  ) : !dailySessionNotesInfo && !dailySessionNotesInfoResponse.loading ? (
    <Typography
      sx={{ fontSize: '18px !important' }}
      variant="h6"
    >
      {!selectedPatient
        ? 'No Records Found'
        : getNoteResponse?.errorMessage?.code === 'BAD_REQUEST' ||
          getNoteResponse?.errorMessage?.code === 'INTERNAL_ERROR'
        ? getNoteResponse?.errorMessage?.message
        : `Billing codes are not available for this patient - "${selectedPatientName}"`}
    </Typography>
  ) : !selectedPatient?.activePayerPresent && !payerPresent && isMultiPayer ? (
    <Box sx={{ textAlign: 'center', mt: 4 }}>
      <Typography
        variant="h5"
        sx={{ mb: 3, fontWeight: 600 }}
      >
        {' '}
        Missing Payer
      </Typography>
      <Typography
        variant="h6"
        sx={{ mb: 3, fontWeight: 600 }}
      >
        This patient does not have an active payer on file. Do you want to proceed with documenting
        the note?
      </Typography>
      <Stack
        direction="row"
        justifyContent="end"
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleTakeNoteAnyway}
          data-testid={dsnPageTestId('take-note-anyway')}
        >
          Take Note Anyway
        </Button>
      </Stack>
    </Box>
  ) : (
    <>
      <Seo title="Patient: Session Notes" />

      {isDocLoader.loader && (
        <Grid sx={loaderCSS}>
          <img
            src={loader}
            alt="Loading..."
          />
        </Grid>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: selectedPatient?.name || selectedPatient?.notesId ? 1 : 0,
        }}
      >
        <Stack
          justifyContent="space-between"
          flexDirection="row"
          alignItems="center"
        >
          <Typography
            sx={{ fontSize: '18px !important' }}
            variant="h6"
          >
            {selectedPatient && selectedPatientName
              ? selectedPatientName?.endsWith("'s")
                ? selectedPatientName
                : `${selectedPatientName}'s` + ' notes'
              : 'No Records Found'}
            {isCopyCodeDescriptionEnabled && (
              <Button
                startIcon={<ContentCopyIcon />}
                onClick={handleTextBankOpen}
                data-testid={dsnPageTestId('text-copy')}
              >
                Text Copy
              </Button>
            )}
          </Typography>

          {!selectedPatient?.notesId && !selectedPatient?.name && (
            <Stack
              flexDirection="row"
              gap="1rem"
            >
              <LoadingButton
                color="primary"
                loading={soapNotesResponse?.loading}
                variant="contained"
                onClick={createSoapNotes}
                // THIN-7916: cannot start a note for a future date of service (facility-local).
                disabled={
                  lockOtherPatient?.response?.data ||
                  moment(selectedDate).format('YYYY-MM-DD') > getFacilityTodayYmd()
                }
              >
                Start Note
              </LoadingButton>
            </Stack>
          )}
          {selectedPatient && (selectedPatient?.name || selectedPatient?.notesId) && (
            <Stack
              flexDirection="row"
              gap="1rem"
              alignItems="center"
            >
              {dailySessionNotesInfo?.dailySessionNote?.status !== 'NOT_STARTED' &&
                soapNotesResponse?.response?.code !== 'BAD_REQUEST' && (
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={handleBack}
                    data-testid={dsnPageTestId('back')}
                  >
                    Back
                  </Button>
                )}

              {soapNotesResponse?.response?.code !== 'BAD_REQUEST' && (
                <>
                  {canDelete &&
                    isDeletableDsnStatus(selectedPatient?.status) &&
                    selectedPatient?.deletable && (
                      <Tooltip title="Delete Note">
                        <IconButton
                          color="error"
                          onClick={handleDeleteNote}
                          data-testid={dsnPageTestId('delete-note')}
                          sx={{
                            borderRadius: '11.765px',
                            background: 'var(--error-light, #FEE4E2)',
                          }}
                        >
                          <TrashIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  <Button
                    color="primary"
                    variant="outlined"
                    startIcon={<ArticleOutlinedIcon />}
                    onClick={() => setOpen((prev: any) => !prev)}
                    disabled={dailySessionNotesInfoResponse?.loading}
                    data-testid={dsnPageTestId('therapy-info')}
                  >
                    Therapy Info
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => handleOpenDsnSummary(selectedPatient)}
                    variant="outlined"
                    data-testid={dsnPageTestId('dsn-summary')}
                  >
                    DSN Summary
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Stack>
        <Box sx={{ display: 'flex', height: '100%' }}>
          <CssBaseline />
          <DrawerMain open={openTextBank}>
            {(getNoteTemplate.loading &&
              dailySessionNotesInfo?.dailySessionNote?.status === 'NOT_STARTED') ||
            dailySessionNotesInfoResponse?.loading ? (
              <img
                src={loader}
                alt="Loading..."
                style={{
                  width: '150px',
                  margin: 'auto',
                  alignContent: 'center',
                  marginTop: '10%',
                  display: 'block',
                }}
              />
            ) : selectedPatient?.locked ? (
              <>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                  py={3}
                >
                  <img
                    src={EmptyState}
                    alt=""
                  />
                  <Typography
                    variant="h5"
                    color="initial"
                    sx={{ color: '#6C737F' }}
                  >
                    The editing period for this Daily Session Note has ended. Contact your DOR for
                    editing permissions.{' '}
                  </Typography>
                </Box>
              </>
            ) : (
              (selectedPatient?.name || selectedPatient?.notesId) &&
              pullUuidResponse?.response &&
              formContainerRef !== null && <>{formElement()}</>
            )}
          </DrawerMain>

          <Box sx={{ marginTop: '50px' }}>
            <CopyTextBank
              open={openTextBank}
              handleDrawerClose={handleTextBankClose}
              source="under_dsn"
            />
          </Box>
        </Box>
      </Box>
      {!selectedPatient?.name && soapNotesResponse?.response?.code === 'BAD_REQUEST' && (
        <Stack sx={{ marginTop: '2rem' }}>
          <Typography variant="h5">{soapNotesResponse?.response?.message}</Typography>
        </Stack>
      )}
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={handleCloseBack}
        aria-labelledby="customized-dialog-title"
        open={openBackModal}
      >
        <BackModalConfirmation
          close={handleCloseBack}
          confirm={() => setOpenEditNotes(false)}
        />
      </BootstrapDialog>
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '520px' } }}
        onClose={() => setOpenDeleteModal(false)}
        aria-labelledby="delete-note-dialog-title"
        open={openDeleteModal}
      >
        <DeleteNoteConfirmation
          close={() => setOpenDeleteModal(false)}
          confirm={handleConfirmDeleteNote}
          patientName={selectedPatientName?.trim()}
          dateOfService={selectedDate ? moment(selectedDate).format('MMMM D, YYYY') : ''}
          therapistName={`${firstName || ''} ${lastName || ''}`.trim()}
          facility={facilityName}
          loading={deleteDsnState?.loading}
        />
      </BootstrapDialog>
      {!soapNotesResponse?.isNoteUpdated &&
        soapNotesResponse?.response?.code === 'INTERNAL_ERROR' && (
          <Box>
            <SucessModal
              close={handleCloseModal}
              isSucess={false}
              title="Error"
              message={soapNotesResponse?.response?.message}
            />
          </Box>
        )}
      <DSNSummaryModal
        open={openSummary}
        title={`DSN Summary | ${discipline}`}
        onClose={() => setOpenSummary(false)}
        data={dsnSummary?.response}
      />
    </>
  );
}

export default FinalizeNotes;
